import {
  forwardRef, useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';

/**
 * Диалог для перетаскиваемых модалок (FormModal с draggable).
 * Подменяет дефолтный ModalDialog из react-bootstrap через проп dialogAs:
 * DOM полностью повторяет дефолтный (div.modal-dialog > div.modal-content),
 * но внутри content первым ребёнком рендерится полоска-ручка — титулбар
 * в духе окна Windows: заголовок слева, крестик закрытия справа, вся полоска
 * перетаскивает окно (место зарезервировано всегда, подсветка — на hover).
 *
 * Смещение применяется через inline transform + transition: none только когда
 * окно реально сдвинуто: transform у .modal-dialog занят анимацией появления
 * Bootstrap (.modal.fade .modal-dialog), inline-стиль её гасит, поэтому при
 * нулевом смещении стиль не ставится вовсе — анимация входа сохраняется.
 *
 * Дополнительно: двойной клик по титулбару сворачивает окно в титулбар
 * (тело и футер скрываются через CSS, компоненты не размонтируются —
 * значения полей Formik сохраняются), а за уголок в правом нижнем углу
 * окно можно ресайзить (size применяется width на .modal-dialog + height
 * на .modal-content, поэтому .modal-body скроллится, а не ломает вёрстку).
 *
 * На тач-раскладке (< 768px или pointer: coarse) drag/resize/collapse
 * выключены, а свайп вниз за титулбар закрывает модалку — как у bottom sheet.
 */

/** Минимум пикселей окна, которые должны оставаться в зоне видимости по горизонтали. */
const MIN_VISIBLE_X = 80;
/** Минимальный размер окна при ресайзе. */
const MIN_WIDTH = 320;
const MIN_HEIGHT = 160;
/** Максимум размера при ресайзе — почти весь вьюпорт. */
const MAX_SIZE_MARGIN = 20;
/** Порог свайпа вниз для закрытия bottom sheet (px). */
const SWIPE_CLOSE_THRESHOLD = 80;
/** Альтернативный порог закрытия — скорость рывка вниз (px/ms): короткий,
 *  но быстрый жест тоже закрывает шторку. */
const SWIPE_CLOSE_VELOCITY = 0.5;
/** Шаг перемещения/ресайза окна с клавиатуры (px); с Shift — в 4 раза больше. */
const KEYBOARD_STEP = 10;

interface DraggableModalDialogProps {
  /** Реакция react-bootstrap на mousedown внутри диалога (клики по фону). Обязана сохраниться. */
  onMouseDown?: (event: React.MouseEvent) => void,
  /** dialogClassName модалки. */
  className?: string,
  /** contentClassName модалки. */
  contentClassName?: string,
  size?: 'sm' | 'lg' | 'xl',
  centered?: boolean,
  scrollable?: boolean,
  /** Заголовок в полоске (FormModal при draggable передаёт сюда свой header). */
  handleTitle?: React.ReactNode,
  /** id заголовка в полоске — сохраняет aria-labelledby модалки (штатный header скрыт). */
  handleTitleId?: string,
  /** Закрытие по крестику в полоске (FormModal при draggable передаёт onClose). */
  onHandleClose?: () => void,
  children?: React.ReactNode,
}

interface DragState {
  pointerId: number,
  startClientX: number,
  startClientY: number,
  /** Смещение на момент захвата. */
  baseX: number,
  baseY: number,
  /** Позиция диалога на момент захвата (уже включает baseX/baseY). */
  rect: DOMRect,
}

interface ResizeState {
  pointerId: number,
  startClientX: number,
  startClientY: number,
  /** Размер на момент захвата. */
  baseWidth: number,
  baseHeight: number,
}

interface SwipeState {
  pointerId: number,
  startClientY: number,
  /** Последняя позиция/время pointermove — для вычисления скорости рывка. */
  lastClientY: number,
  lastTime: number,
}

/**
 * Ограничение смещения: верх окна не уходит за экран (ручка должна оставаться
 * доступной), по горизонтали минимум MIN_VISIBLE_X пикселей остаётся на экране.
 * rect снят при смещении baseX/baseY.
 */
const clampOffset = (x: number, y: number, rect: DOMRect, baseX: number, baseY: number) => {
  const minX = baseX + MIN_VISIBLE_X - rect.width - rect.left;
  const maxX = baseX + window.innerWidth - MIN_VISIBLE_X - rect.left;
  const minY = baseY - rect.top;

  return {
    x: Math.min(Math.max(x, minX), maxX),
    y: Math.max(y, minY),
  };
};

const DraggableModalDialog = forwardRef<HTMLDivElement, DraggableModalDialogProps>((props, ref) => {
  const {
    className,
    contentClassName,
    size,
    centered,
    scrollable,
    onMouseDown,
    handleTitle,
    handleTitleId,
    onHandleClose,
    children,
    ...rest
  } = props;

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dialogSize, setDialogSize] = useState<{ width: number, height: number } | null>(null);
  // На тач-устройствах и узких экранах (< 768px, тот же порог, что в _modals.scss)
  // drag/resize/collapse отключаются: модалка деградирует в обычный статичный
  // header. Запрос через запятую = логическое ИЛИ.
  const [touchLayout, setTouchLayout] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(hover: none) and (pointer: coarse), (max-width: 767.98px)').matches
  );
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<DragState | null>(null);
  const resizeState = useRef<ResizeState | null>(null);
  const swipeState = useRef<SwipeState | null>(null);
  // Свайп вниз за титулбар в мобильном режиме: сколько пикселей диалог утянут (0 — на месте).
  const [swipeY, setSwipeY] = useState(0);
  // Возврат шторки на место после отпускания — анимированный (transition), а не скачок.
  const [swipeReturn, setSwipeReturn] = useState(false);

  const setDialogRef = useCallback((node: HTMLDivElement | null) => {
    dialogRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  }, [ref]);

  // Прямоугольник видимого окна. Смещение transform вешается на .modal-dialog,
  // но при centered сам диалог — flex-контейнер почти на весь вьюпорт
  // (.modal-dialog-centered: min-height ~100%), карточка центрируется внутри него.
  // Кламп по rect диалога в этом режиме запрещал бы движение вверх почти сразу
  // («не поднять выше середины»), поэтому считаем по .modal-content.
  const getBox = useCallback((): DOMRect | null => {
    const node = dialogRef.current;
    if (!node) return null;
    const content = node.querySelector('.modal-content');
    return (content ?? node).getBoundingClientRect();
  }, []);

  // При ресайзе окна центрирование Bootstrap пересчитывается — пересжимаем смещение в экран.
  useEffect(() => {
    const onResize = () => {
      const rect = getBox();
      if (!rect) return;
      setOffset((prev) => clampOffset(prev.x, prev.y, rect, prev.x, prev.y));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [getBox]);

  // Переход между десктопом и мобильным режимом (сужение окна или тач-указатель).
  useEffect(() => {
    const media = window.matchMedia('(hover: none) and (pointer: coarse), (max-width: 767.98px)');
    const onChange = (event: MediaQueryListEvent) => {
      setTouchLayout(event.matches);
      // В мобильном режиме окно прижато к низу/на весь экран — сдвиг не нужен.
      if (event.matches) setOffset({ x: 0, y: 0 });
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = getBox();
    if (!rect || event.button !== 0) return;

    // На тач-раскладке вместо перетаскивания — свайп вниз за титулбар:
    // утянуть шторку и отпустить = закрыть (порог SWIPE_CLOSE_THRESHOLD).
    if (touchLayout) {
      setSwipeReturn(false);
      swipeState.current = {
        pointerId: event.pointerId,
        startClientY: event.clientY,
        lastClientY: event.clientY,
        lastTime: event.timeStamp,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    event.preventDefault();
    dragState.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      baseX: offset.x,
      baseY: offset.y,
      rect,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const swipe = swipeState.current;
    if (swipe && event.pointerId === swipe.pointerId) {
      // Шторку тянем только вниз; вверх — на месте.
      setSwipeY(Math.max(0, event.clientY - swipe.startClientY));
      swipe.lastClientY = event.clientY;
      swipe.lastTime = event.timeStamp;
      return;
    }

    const state = dragState.current;
    if (!state || event.pointerId !== state.pointerId) return;

    setOffset(clampOffset(
      state.baseX + (event.clientX - state.startClientX),
      state.baseY + (event.clientY - state.startClientY),
      state.rect,
      state.baseX,
      state.baseY
    ));
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const swipe = swipeState.current;
    if (swipe && event.pointerId === swipe.pointerId) {
      const dy = event.clientY - swipe.startClientY;
      // Скорость на последнем отрезке жеста: короткий быстрый рывок вниз
      // закрывает шторку даже без достижения порога по дистанции.
      const dt = event.timeStamp - swipe.lastTime;
      const velocity = dt > 0 ? (event.clientY - swipe.lastClientY) / dt : 0;
      swipeState.current = null;
      // Возврат шторки — плавный. При закрытии фейд Bootstrap идёт параллельно
      // с возвратом: окно не «прыгает» на место перед исчезновением.
      setSwipeReturn(true);
      setSwipeY(0);
      if (dy >= SWIPE_CLOSE_THRESHOLD || velocity >= SWIPE_CLOSE_VELOCITY) onHandleClose?.();
      return;
    }

    const state = dragState.current;
    if (!state || event.pointerId !== state.pointerId) return;

    dragState.current = null;
    setDragging(false);
  };

  // Клавиатурное перемещение окна за титулбар: стрелки — шаг (Shift — крупнее),
  // Enter/Space — свернуть/развернуть (аналог двойного клика мышью).
  const handleDragKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (touchLayout) return;

    const step = event.shiftKey ? KEYBOARD_STEP * 4 : KEYBOARD_STEP;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown': {
        const rect = getBox();
        if (!rect) return;
        const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
        const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
        setOffset((prev) => clampOffset(prev.x + dx, prev.y + dy, rect, prev.x, prev.y));
        break;
      }
      case 'Enter':
      case ' ':
        setCollapsed((prev) => !prev);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  // Клавиатурный ресайз за уголок: стрелки меняют размер на шаг (Shift — крупнее).
  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (touchLayout) return;

    const step = event.shiftKey ? KEYBOARD_STEP * 4 : KEYBOARD_STEP;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown': {
        const rect = getBox();
        if (!rect) return;
        const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - MAX_SIZE_MARGIN);
        const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - MAX_SIZE_MARGIN);
        const baseWidth = dialogSize?.width ?? rect.width;
        const baseHeight = dialogSize?.height ?? rect.height;
        setDialogSize({
          width: Math.min(Math.max(
            baseWidth + (event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0),
            MIN_WIDTH
          ), maxWidth),
          height: Math.min(Math.max(
            baseHeight + (event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0),
            MIN_HEIGHT
          ), maxHeight),
        });
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = getBox();
    if (!rect || event.button !== 0 || touchLayout) return;

    event.preventDefault();
    event.stopPropagation();
    resizeState.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      baseWidth: rect.width,
      baseHeight: rect.height,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = resizeState.current;
    if (!state || event.pointerId !== state.pointerId) return;

    const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - MAX_SIZE_MARGIN);
    const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - MAX_SIZE_MARGIN);
    setDialogSize({
      width: Math.min(Math.max(
        state.baseWidth + (event.clientX - state.startClientX),
        MIN_WIDTH
      ), maxWidth),
      height: Math.min(Math.max(
        state.baseHeight + (event.clientY - state.startClientY),
        MIN_HEIGHT
      ), maxHeight),
    });
  };

  const handleResizePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = resizeState.current;
    if (!state || event.pointerId !== state.pointerId) return;

    resizeState.current = null;
  };

  const hasOffset = offset.x !== 0 || offset.y !== 0;

  return (
    <div
      { ...rest }
      ref={ setDialogRef }
      onMouseDown={ onMouseDown }
      className={ classNames(
        'modal-dialog',
        className,
        size && `modal-${ size }`,
        centered && 'modal-dialog-centered',
        scrollable && 'modal-dialog-scrollable',
        dragging && 'modal-dialog-dragging',
        collapsed && 'modal-dialog-collapsed',
        dialogSize !== null && 'modal-dialog-resized'
      ) }
      style={ {
        ...(hasOffset ? {
          transform: `translate(${ offset.x }px, ${ offset.y }px)`,
          transition: 'none',
        } : null),
        // Свайп вниз в мобильном режиме: пока тянут — сдвиг без анимации;
        // при отпускании swipeY сбрасывается в 0 и шторка возвращается на место.
        ...(swipeY > 0 || swipeReturn ? {
          transform: `translateY(${ swipeY }px)`,
          transition: swipeReturn ? 'transform 0.2s ease-out' : 'none',
        } : null),
        // Размер задаётся шириной диалога: Bootstrap держит .modal-dialog в max-width,
        // поэтому при пользовательском размере его снимаем.
        ...(dialogSize ? { width: `${ dialogSize.width }px`, maxWidth: 'none' } : null),
      } }
    >
      <div
        className={ classNames('modal-content', contentClassName) }
        style={ dialogSize && !collapsed ? { height: `${ dialogSize.height }px` } : undefined }
      >
        <div
          className='modal-drag-handle'
          role='button'
          tabIndex={ touchLayout ? undefined : 0 }
          aria-label='Переместить окно: стрелки — переместить, с Shift — быстрее, Enter — свернуть или развернуть'
          onPointerDown={ handlePointerDown }
          onPointerMove={ handlePointerMove }
          onPointerUp={ handlePointerEnd }
          onPointerCancel={ handlePointerEnd }
          onKeyDown={ handleDragKeyDown }
          onDoubleClick={ touchLayout ? undefined : () => setCollapsed((prev) => !prev) }
        >
          { handleTitle ? (
            <div className='modal-drag-handle-title' id={ handleTitleId }>
              { handleTitle }
            </div>
          ) : null }
          { onHandleClose && (
            <button
              type='button'
              className='modal-drag-handle-close'
              aria-label='Закрыть'
              onClick={ onHandleClose }
              onPointerDown={ (event) => event.stopPropagation() }
            >
              <span aria-hidden='true'>×</span>
            </button>
          ) }
        </div>
        { children }
        <div
          className='modal-resize-handle'
          role='button'
          tabIndex={ touchLayout ? undefined : 0 }
          aria-label='Изменить размер окна: стрелки — размер, с Shift — быстрее'
          onPointerDown={ handleResizePointerDown }
          onPointerMove={ handleResizePointerMove }
          onPointerUp={ handleResizePointerEnd }
          onPointerCancel={ handleResizePointerEnd }
          onKeyDown={ handleResizeKeyDown }
        />
      </div>
    </div>
  );
});

DraggableModalDialog.displayName = 'DraggableModalDialog';

export default DraggableModalDialog;
